import type {Reviewer} from '../../../page-data/payloads/review-requests'
import styles from './Reviewers.module.css'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {Suspense} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ReviewerIdentity} from './ReviewerIdentity'

import {useReviewers} from '../data/use-reviewers'
import {SidebarSection} from '../SidebarSection'
import {reviewState} from './review-state-helpers'
import {computeTeamCompositeStates, type TeamCompositeState} from './team-review-state-helpers'

export function TeamReviewSummary() {
  return (
    <ErrorBoundary boundaryName="TeamReviewSummary" fallback={null}>
      <Suspense fallback={null}>
        <TeamReviewSummaryComponent />
      </Suspense>
    </ErrorBoundary>
  )
}

export function TeamReviewSummaryComponent() {
  const {data: reviewers} = useReviewers()
  const teamStates = computeTeamCompositeStates(reviewers)

  if (teamStates.length === 0) {
    return null
  }

  return (
    <SidebarSection title="Team review summary">
      <ErrorBoundary
        boundaryName="TeamReviewSummaryComponent"
        fallback={<span className={styles.emptyState}>Unable to load team reviews</span>}
      >
        <ul className={clsx(styles.reviewerList, 'mt-2')}>
          {teamStates.map(team => (
            <TeamCompositeRow key={team.teamName} team={team} />
          ))}
        </ul>
      </ErrorBoundary>
    </SidebarSection>
  )
}

function TeamCompositeRow({team}: {team: TeamCompositeState}) {
  const {icon: stateIcon, className: stateClass} = reviewState(team.compositeState)
  const syntheticReviewer: Reviewer = team.teamReviewer ?? {
    id: 0,
    displayLogin: team.teamName,
    name: null,
    avatarUrl: '',
    reviewerType: 'Team' as const,
    organizationLogin: null,
  }
  const hasAvatar = syntheticReviewer.avatarUrl !== ''
  const nestedUsers = [...team.completedUserReviews, ...team.pendingUserRequests].map(r => r.reviewer)

  return (
    <>
      <li className={styles.reviewerRow}>
        {hasAvatar ? (
          <ReviewerIdentity reviewer={syntheticReviewer} />
        ) : (
          <span className={styles.reviewerIdentity}>
            <span className={styles.reviewerName}>{team.teamName}</span>
          </span>
        )}
        <span className={styles.indicators}>
          <IconButton
            icon={stateIcon}
            aria-label={`${team.teamName}: ${team.stateLabel}`}
            variant="invisible"
            size="small"
            tooltipDirection="nw"
            className={clsx(styles.statusIcon, stateClass)}
          />
        </span>
      </li>
      {nestedUsers.length > 0 && (
        <li className={clsx(styles.reviewerRow, styles.nestedReviewerItem)}>
          <span className={clsx(styles.connector, styles.connectorLast)} aria-hidden="true" />
          <span className={styles.nestedUserList}>
            {nestedUsers.map((reviewer, i, arr) => (
              <span key={reviewer.id} className={styles.nestedUserEntry}>
                <ReviewerIdentity reviewer={reviewer} />
                {i < arr.length - 1 && <span className={styles.separator}>, </span>}
              </span>
            ))}
          </span>
        </li>
      )}
    </>
  )
}
